import React, { useRef, useEffect } from 'react';
import styles from './index.module.less';
import isEmpty from 'lodash/isEmpty';
import { EssayClassifies, HomeArticleModel } from '@/types/home';
import HomeFullItem from '../Home/HomeFullItem';
import CategoryTopContent from './Header';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';

const CategoryContent = ({ data }: { data: any }) => {
  const { t } = useTranslation('common');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const articleListRes = data['articleList'];
  const articleList: HomeArticleModel[] = articleListRes?.data;
  const bookList: HomeArticleModel[] = data['bookList'];
  const totalSize: number = data['totalSize'];
  const pageNo: number = Number(data['pageNo']);
  const classifies: EssayClassifies[] = data['classifies'];
  const pageSize: number = data['pageSize'];
  const type: string = data['type'] || '';
  const showTopContent: boolean = data['showTopContent'] ?? false; // ✅ 从服务端获取判断结果
  const totalPage: number = Math.ceil(totalSize / pageSize);

  // Generate pagination data with hrefs
  const generatePaginationData = () => {
    // Generate page numbers
    let pageNumbers: (number | 'ellipsis')[] = [];
    if (totalPage <= 5) {
      pageNumbers = Array.from({ length: totalPage }, (_, i) => i + 1);
    } else if (pageNo <= 3) {
      pageNumbers = [1, 2, 3, 4, 5];
    } else if (pageNo >= totalPage - 2) {
      pageNumbers = [totalPage - 4, totalPage - 3, totalPage - 2, totalPage - 1, totalPage];
    } else {
      pageNumbers = [1, 'ellipsis', pageNo - 1, pageNo, pageNo + 1, 'ellipsis', totalPage];
    }
    const pagePrefix = type ? `/blog/category/${type.toString().replace(' ', '-')}/page` : '/blog/page';

    return {
      pageNumbers,
      hrefs: {
        firstPage: `${pagePrefix}/1`,
        previousPage: `${pagePrefix}/${pageNo - 1}`,
        nextPage: `${pagePrefix}/${pageNo + 1}`,
        lastPage: `${pagePrefix}/${totalPage}`,
        getPageHref: (page: number) => `${pagePrefix}/${page}`,
      },
      showFirstPage: pageNo > 1,
      showLastPage: pageNo < totalPage,
      showPreviousPage: pageNo > 1,
      showNextPage: pageNo < totalPage,
    };
  };

  const paginationData = generatePaginationData();

  // Generate category link
  const generateCategoryLink = (k: string) => {
    const value = k === '0' || !k ? '' : k;
    const pathType = value.toString().replace(' ', '-');
    if (pathType) {
      return `/blog/category/${pathType}`;
    } else {
      return '/blog';
    }
  };

  // 电脑鼠标滚轮水平滚动控制
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleWheel = (e: WheelEvent) => {
      // 检查是否需要滚动（内容溢出）
      if (scrollContainer.scrollWidth > scrollContainer.clientWidth) {
        // 阻止默认垂直滚动
        e.preventDefault();

        // 根据滚轮增量进行水平滚动
        const scrollAmount = e.deltaY > 0 ? 50 : -50;
        scrollContainer.scrollLeft += scrollAmount;
      }
    };

    scrollContainer.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      scrollContainer.removeEventListener('wheel', handleWheel);
    };
  }, []);

  return (
    <div className={styles.container}>
      {/* <h1 style={{ display: 'none' }}>{t("Erasa's Blog")}</h1> */}
      <div className={styles.utilsTop} ref={scrollContainerRef}>
        <div className={styles.taxonomicGroup}>
          {/* Link 按钮组 - 移动端和桌面端统一 */}
          <div className={styles.classifyBtnGroup}>
            <Link href={generateCategoryLink('')} className={`${styles.classifyBtn} ${!type && styles.activeBtn}`}>
              Newest
            </Link>
            {classifies?.map((item) => (
              <Link
                key={item.k}
                href={generateCategoryLink(item.k)}
                className={`${styles.classifyBtn} ${type === item.k && styles.activeBtn}`}
              >
                {item.v}
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className={styles.homeContent}>
        {/* ✅ 使用服务端判断结果，避免客户端条件渲染导致闪烁 */}
        {showTopContent ? (
          <CategoryTopContent list={bookList} />
        ) : (
          <img
            style={{ width: '100%', height: 'auto', marginBottom: '50px' }}
            src="/images/article/heroImage.webp"
            alt=""
          />
        )}

        <div className={styles.lineContainer}>
          <span>{t('latest')}</span>
        </div>

        <div className={styles.leftContainer}>
          {isEmpty(articleList) ? (
            <>
              <HomeFullItem />
              <HomeFullItem />
              <HomeFullItem />
              <HomeFullItem />
            </>
          ) : (
            <>
              {(articleList ?? []).map((item, index) => {
                return (
                  <HomeFullItem
                    key={item.article_id}
                    articleModel={item}
                    showBottomLine={index != articleList.length - 1}
                    showPadding={false}
                  />
                );
              })}

              {/* Pagination */}
              {totalPage > 1 && (
                <div className={styles.pagination}>
                  {/* First Page Button */}
                  {paginationData.showFirstPage && (
                    <Link href={paginationData.hrefs.firstPage} className={styles.navigationButton}>
                      <svg width="15" height="15" viewBox="0 0 15 15" fill="currentColor">
                        <path
                          d="M6.85355 3.85355C7.04882 3.65829 7.04882 3.34171 6.85355 3.14645C6.65829 2.95118 6.34171 2.95118 6.14645 3.14645L2.14645 7.14645C1.95118 7.34171 1.95118 7.65829 2.14645 7.85355L6.14645 11.8536C6.34171 12.0488 6.65829 12.0488 6.85355 11.8536C7.04882 11.6583 7.04882 11.3417 6.85355 11.1464L3.20711 7.5L6.85355 3.85355ZM12.8536 3.85355C13.0488 3.65829 13.0488 3.34171 12.8536 3.14645C12.6583 2.95118 12.3417 2.95118 12.1464 3.14645L8.14645 7.14645C7.95118 7.34171 7.95118 7.65829 8.14645 7.85355L12.1464 11.8536C12.3417 12.0488 12.6583 12.0488 12.8536 11.8536C13.0488 11.6583 13.0488 11.3417 12.8536 11.1464L9.20711 7.5L12.8536 3.85355Z"
                          fill="currentColor"
                          fillRule="evenodd"
                          clipRule="evenodd"
                        />
                      </svg>
                    </Link>
                  )}

                  {/* Previous Button with Arrow */}
                  {paginationData.showPreviousPage && (
                    <Link rel="prev" href={paginationData.hrefs.previousPage} className={styles.navigationButton}>
                      <svg width="15" height="15" viewBox="0 0 15 15" fill="currentColor">
                        <path
                          d="M8.84182 3.13514C9.04327 3.32401 9.05348 3.64042 8.86462 3.84188L5.43521 7.49991L8.86462 11.1579C9.05348 11.3594 9.04327 11.6758 8.84182 11.8647C8.64036 12.0535 8.32394 12.0433 8.13508 11.8419L4.38508 7.84188C4.20477 7.64955 4.20477 7.35027 4.38508 7.15794L8.13508 3.15794C8.32394 2.95648 8.64036 2.94628 8.84182 3.13514Z"
                          fill="currentColor"
                          fillRule="evenodd"
                          clipRule="evenodd"
                        />
                      </svg>
                    </Link>
                  )}

                  {/* Page Numbers */}
                  {paginationData.pageNumbers.map((page: number | 'ellipsis', index: number) => {
                    if (page === 'ellipsis') {
                      return (
                        <div key={`ellipsis-${index}`} className={`${styles.paginationItem} ${styles.ellipsis}`}>
                          ...
                        </div>
                      );
                    }

                    return (
                      <Link
                        key={page}
                        href={paginationData.hrefs.getPageHref(page)}
                        className={`${styles.paginationItem} ${page === pageNo ? styles.active : ''}`}
                      >
                        {page}
                      </Link>
                    );
                  })}

                  {/* Next Button with Arrow */}
                  {paginationData.showNextPage && (
                    <Link rel="next" href={paginationData.hrefs.nextPage} className={styles.navigationButton}>
                      <svg width="15" height="15" viewBox="0 0 15 15" fill="currentColor">
                        <path
                          d="M6.1584 3.13508C6.35985 2.94621 6.67627 2.95642 6.86514 3.15788L10.6151 7.15788C10.7954 7.3502 10.7954 7.64949 10.6151 7.84182L6.86514 11.8418C6.67627 12.0433 6.35985 12.0535 6.1584 11.8646C5.95694 11.6757 5.94673 11.3593 6.1356 11.1579L9.565 7.49985L6.1356 3.84182C5.94673 3.64036 5.95694 3.32394 6.1584 3.13508Z"
                          fill="currentColor"
                          fillRule="evenodd"
                          clipRule="evenodd"
                        />
                      </svg>
                    </Link>
                  )}

                  {/* Last Page Button */}
                  {paginationData.showLastPage && (
                    <Link href={paginationData.hrefs.lastPage} className={styles.navigationButton}>
                      <svg width="15" height="15" viewBox="0 0 15 15" fill="currentColor">
                        <path
                          d="M2.14645 11.1464C1.95118 11.3417 1.95118 11.6583 2.14645 11.8536C2.34171 12.0488 2.65829 12.0488 2.85355 11.8536L6.85355 7.85355C7.04882 7.65829 7.04882 7.34171 6.85355 7.14645L2.85355 3.14645C2.65829 2.95118 2.34171 2.95118 2.14645 3.14645C1.95118 3.34171 1.95118 3.65829 2.14645 3.85355L5.79289 7.5L2.14645 11.1464ZM8.14645 11.1464C7.95118 11.3417 7.95118 11.6583 8.14645 11.8536C8.34171 12.0488 8.65829 12.0488 8.85355 11.8536L12.8536 7.85355C13.0488 7.65829 13.0488 7.34171 12.8536 7.14645L8.85355 3.14645C8.65829 2.95118 8.34171 2.95118 8.14645 3.14645C7.95118 3.34171 7.95118 3.65829 8.14645 3.85355L11.7929 7.5L8.14645 11.1464Z"
                          fill="currentColor"
                          fillRule="evenodd"
                          clipRule="evenodd"
                        />
                      </svg>
                    </Link>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoryContent;
